/**
 * Theme Utility Mixins
 * Reusable style patterns from Minimal template
 *
 * Usage:
 * import { textGradient, bgBlur, maxLine } from 'src/theme/mixins';
 *
 * sx={{
 *   ...textGradient('120deg, #00A76F 0%, #00B8D9 100%'),
 * }}
 */

// ===========================
// TEXT GRADIENT
// ===========================

/**
 * Creates gradient text effect
 * @param {string} color - Gradient color string (e.g., 'to right, #00A76F, #00B8D9' or '120deg, #00A76F 0%, #00B8D9 100%')
 * @returns {object} - Style object for gradient text
 *
 * @example
 * <Box sx={{ ...textGradient('120deg, #00A76F 0%, #00B8D9 100%') }}>
 *   Gradient Text
 * </Box>
 */
export const textGradient = (color) => ({
  background: `linear-gradient(${color})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  color: 'transparent',
});

// ===========================
// BACKGROUND GRADIENT
// ===========================

/**
 * Creates background gradient with optional image overlay
 * @param {object} options
 * @param {string} options.color - Gradient color string
 * @param {string} [options.imgUrl] - Optional background image URL
 * @returns {object} - Style object for background gradient
 *
 * @example
 * <Box sx={{
 *   ...bgGradient({
 *     color: 'to right, #00A76F, #00B8D9',
 *     imgUrl: '/assets/background.png'
 *   })
 * }}>
 */
export const bgGradient = ({ color, imgUrl }) => {
  if (imgUrl) {
    return {
      background: `linear-gradient(${color}), url(${imgUrl})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    };
  }
  return {
    background: `linear-gradient(${color})`,
  };
};

// ===========================
// BACKGROUND BLUR (Glassmorphism)
// ===========================

/**
 * Creates glassmorphism effect with backdrop blur
 * @param {object} options
 * @param {string} options.color - Background color with alpha (e.g., 'rgba(255, 255, 255, 0.8)')
 * @param {number} [options.blur=6] - Blur amount in pixels
 * @returns {object} - Style object for glassmorphism effect
 *
 * @example
 * <Box sx={{
 *   ...bgBlur({
 *     color: 'rgba(255, 255, 255, 0.8)',
 *     blur: 10
 *   })
 * }}>
 */
export const bgBlur = ({ color, blur = 6 }) => ({
  backdropFilter: `blur(${blur}px)`,
  WebkitBackdropFilter: `blur(${blur}px)`,
  backgroundColor: color,
});

// ===========================
// MAX LINE (Text Truncation)
// ===========================

/**
 * Truncates text to max number of lines with ellipsis
 * @param {object} options
 * @param {number} [options.line=2] - Maximum number of lines
 * @param {object} [options.persistent] - Typography object for height calculation (e.g., theme.typography.body2)
 * @returns {object} - Style object for text truncation
 *
 * @example
 * <Typography sx={{
 *   ...maxLine({ line: 2, persistent: theme.typography.body1 })
 * }}>
 *   Long text that will be truncated...
 * </Typography>
 */
export const maxLine = ({ line = 2, persistent }) => {
  const lineHeight = persistent?.lineHeight || 1.5;
  const fontSize = persistent?.fontSize || 16;

  return {
    overflow: 'hidden',
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    WebkitLineClamp: line,
    WebkitBoxOrient: 'vertical',
    ...(persistent && {
      height: `${parseFloat(fontSize) * lineHeight * line}px`,
    }),
  };
};

// ===========================
// HIDE SCROLLBAR
// ===========================

/**
 * Hides vertical scrollbar while maintaining scroll functionality
 * @type {object}
 *
 * @example
 * <Box sx={{ ...hideScrollY }}>
 *   Scrollable content
 * </Box>
 */
export const hideScrollY = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

/**
 * Hides horizontal scrollbar while maintaining scroll functionality
 * @type {object}
 *
 * @example
 * <Box sx={{ ...hideScrollX }}>
 *   Scrollable content
 * </Box>
 */
export const hideScrollX = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

// ===========================
// PAPER (Glassmorphism Card)
// ===========================

/**
 * Creates elevated paper effect with blur and shadow
 * Useful for cards, modals, dropdowns
 * @param {object} options
 * @param {object} options.theme - MUI theme object
 * @param {boolean} [options.dropdown=false] - Use dropdown shadow instead of card shadow
 * @param {number} [options.blur=20] - Blur amount
 * @returns {object} - Style object for paper effect
 *
 * @example
 * <Paper sx={(theme) => ({
 *   ...paper({ theme, dropdown: true })
 * })}>
 */
export const paper = ({ theme, dropdown = false, blur = 20 }) => ({
  ...bgBlur({
    color: theme.palette.background.paper,
    blur,
  }),
  boxShadow: dropdown ? theme.customShadows.dropdown : theme.customShadows.card,
});

// ===========================
// MENU ITEM
// ===========================

/**
 * Standardized menu item styling
 * @param {object} theme - MUI theme object
 * @returns {object} - Style object for menu items
 *
 * @example
 * <MenuItem sx={(theme) => ({
 *   ...menuItem(theme)
 * })}>
 */
export const menuItem = (theme) => ({
  ...theme.typography.body2,
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.shape.borderRadius * 0.75,
  '&:not(:last-of-type)': {
    marginBottom: 4,
  },
  '&.Mui-selected': {
    fontWeight: theme.typography.fontWeightSemiBold || 600,
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
});

// ===========================
// BORDER GRADIENT
// ===========================

/**
 * Creates gradient border effect using pseudo-element
 * Note: This returns the pseudo-element content to be used in ::before or ::after
 * @param {object} options
 * @param {string} options.color - Gradient color string
 * @param {string} [options.padding='2px'] - Border width
 * @returns {object} - Style object for gradient border
 *
 * @example
 * <Box sx={{
 *   position: 'relative',
 *   '&::before': {
 *     content: '""',
 *     position: 'absolute',
 *     inset: 0,
 *     ...borderGradient({
 *       color: 'to right, #00A76F, #00B8D9',
 *       padding: '2px'
 *     })
 *   }
 * }}>
 */
export const borderGradient = ({ color, padding = '2px' }) => ({
  background: `linear-gradient(${color})`,
  padding,
  borderRadius: 'inherit',
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude',
});
